import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { PaymentMethodVM, PriceRecurringIntervalEnum, SubscriptionStatusEnum, UpcomingInvoicePreviewModel } from '@rma/billing-api';
import { AgencyProfileService, CurrentProfileContext, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { mapPaymentMethod, PaymentMethod, PaymentMethodService } from '@rma/dashboard/billing/public/api-payment-methods';
import {
  CurrentSubscription,
  CurrentSubscriptionService,
  SubscriptionProductService,
} from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { StandardErrorMessage } from '@rma/dashboard/generic/domain/to-be-sorted';
import { CalloutComponent } from '@rma/dashboard/generic/ui/callout';
import { MatErrorOverrideComponent } from '@rma/dashboard/generic/ui/form-error';
import { PrettyCurrencyPipeModule } from '@rma/dashboard/generic/util/pipes/pretty-currency';
import { PageHeaderComponent } from '@rma/dashboard/layout/public/page-header';
import { isIndividual } from '@rma/generic/domain/helpers';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { catchError, combineLatest, delay, filter, map, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';
import { InvoiceService } from '../domain/data-access/invoice.service';
import { BillingContactDetailsComponent } from '../orc-billing-contact-details/billing-contact-details.component';
import { InvoicesComponent } from '../orc-invoices/invoices.component';
import { PaymentMethodsComponent } from '../orc-payment-methods/payment-methods.component';
import { BillingInfoNavComponent } from '../ui-billing-info-nav/billing-info-nav.component';

@Component({
  selector: 'rma-billing-details',
  templateUrl: './billing-details.component.html',
  styleUrls: ['billing-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    DatePipe,
    BillingContactDetailsComponent,
    BillingInfoNavComponent,
    CalloutComponent,
    InvoicesComponent,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatErrorOverrideComponent,
    RmaIconComponent,
    MatProgressSpinnerModule,
    MatTableModule,
    PageHeaderComponent,
    PaymentMethodsComponent,
    PrettyCurrencyPipeModule,
    RouterModule,
    TranslocoModule,
  ],
  providers: [DatePipe, CurrencyPipe, provideTranslocoScope('subscriptions')],
})
export class BillingDetailsComponent {
  protected readonly coupon$: Observable<InvoicePreviewCoupon>;
  protected readonly error$ = new EventEmitter<string | null>();
  protected readonly expiredPaymentMethod$: Observable<boolean | undefined>;
  protected readonly isAgency$: Observable<boolean>;
  protected readonly isOverdue$: Observable<boolean>;
  protected readonly noPaymentMethods$: Observable<boolean | undefined>;
  protected readonly paymentMethods$: Observable<PaymentMethod[]>;
  protected readonly profile$: Observable<CurrentProfileContext>;
  protected readonly subscription$: Observable<CurrentSubscription>;
  protected readonly subscriptionChanges$: Observable<string | null>;
  protected readonly switchPeriod$: Observable<PriceRecurringIntervalEnum>;
  protected readonly trialPeriodDays$: Observable<number>;
  protected readonly upcomingInvoice$: Observable<InvoicePreview | null>;
  protected readonly agentCount$: Observable<number | undefined>;
  protected readonly hidePageHeader = false;
  protected noInvoice = false;

  private refresh$ = new Subject<void>();

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly currentSubscriptionService: CurrentSubscriptionService,
    private readonly invoiceService: InvoiceService,
    private readonly paymentMethodService: PaymentMethodService,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly subscriptionProductService: SubscriptionProductService,
    private readonly agencyProfileService: AgencyProfileService,
    readonly activatedRoute: ActivatedRoute,
  ) {
    this.hidePageHeader = activatedRoute.snapshot.data['hidePageHeader'] ?? false;

    this.profile$ = this.profileContextService.profileContext$;

    this.subscription$ = this.currentSubscriptionService.currentSubscription$.pipe(
      catchError((error) => {
        this.error$.next(error.Message ?? StandardErrorMessage);
        throw error;
      }),
    );

    this.upcomingInvoice$ = this.invoiceService.upcomingInvoice$.pipe(
      map((x) => mapInvoicePreview(x)),
      catchError((error) => {
        if (error.Status === 404) {
          this.noInvoice = true;
          return of(null);
        }
        this.error$.next(error.Message ?? StandardErrorMessage);
        throw error;
      }),
    );

    this.paymentMethods$ = this.refresh$.pipe(
      startWith(true),
      switchMap((_) => this.paymentMethodService.getPaymentMethods()),
      map((paymentMethods: PaymentMethodVM[]) =>
        paymentMethods.map((x) => mapPaymentMethod(x)).sort((x, y) => (!x?.isDefault ? (!y?.isDefault ? 1 : -1) : -1)),
      ),
      rmaShareReplay(),
    );

    this.isOverdue$ = this.subscription$.pipe(map((x) => x.status === SubscriptionStatusEnum.PastDue));

    this.isAgency$ = this.profileContextService.isAgency$;

    this.subscriptionChanges$ = this.subscription$.pipe(
      filter((x) => x.hasCancelled || !!x.newPlan),
      map((subscription) => (subscription.hasCancelled ? 'cancelled' : 'subscription-changes')),
    );

    this.switchPeriod$ = this.subscription$.pipe(
      filter((x) => !!x.productId && !x.hasCancelled),
      switchMap((x) =>
        // SAFETY(legacy): unreviewed, pre-dates this rule.
        this.subscriptionProductService.getCurrentProduct(x.productId as number).pipe(
          map((z) => z?.Prices?.find((y) => y.Interval !== x.period)?.Interval),
          nonNullable(),
        ),
      ),
    );

    this.trialPeriodDays$ = this.subscriptionProductService.availableTrialDays$;

    this.coupon$ = this.upcomingInvoice$.pipe(
      map((upcomingInvoice) => upcomingInvoice?.coupon),
      nonNullable(),
    );

    this.expiredPaymentMethod$ = this.paymentMethods$.pipe(
      switchMap((paymentMethods) => paymentMethods.filter((x) => x.isDefault).map((y) => y.card?.isExpired)),
    );

    this.noPaymentMethods$ = this.paymentMethods$.pipe(map((paymentMethods) => (paymentMethods.length < 1 ? true : false)));

    this.agentCount$ = this.profileContextService.profileIdentifier$.pipe(
      switchMap(({ profileType, profileCode }) => {
        if (isIndividual(profileType)) {
          return of(1);
        }
        return this.agencyProfileService.getAgencyProfileDetails(profileCode).pipe(
          map((agency) => agency?.Agents?.filter((agent) => agent.IsSubscribedViaAgency).length ?? 0),
          catchError(() => of(0)),
        );
      }),
      startWith(0),
      rmaShareReplay(),
    );

    combineLatest([this.subscription$, this.coupon$, this.switchPeriod$, this.upcomingInvoice$, this.paymentMethods$])
      .pipe(take(1), delay(1))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public refresh() {
    this.refresh$.next(undefined);
  }
}

function mapInvoicePreview(invoicePreview: UpcomingInvoicePreviewModel): InvoicePreview {
  return {
    dateDue: invoicePreview.BillingPeriodSummaries?.[0].StartDate,
    amountDue: invoicePreview.AmountDue,
    coupon: invoicePreview.Coupon
      ? {
          couponCode: invoicePreview.Coupon.CouponCode,
          percentOff: invoicePreview.Coupon.PercentOff ?? 0,
          amountOff: invoicePreview.Coupon.AmountOff ? invoicePreview.Coupon.AmountOff / 100 : undefined,
          durationInMonths: invoicePreview.Coupon.DurationInMonths ?? 0,
        }
      : undefined,
  };
}

interface InvoicePreviewCoupon {
  couponCode?: string;
  percentOff?: number;
  amountOff?: number;
  durationInMonths?: number;
}

interface InvoicePreview {
  dateDue?: string | Date;
  amountDue: number;
  coupon?: InvoicePreviewCoupon;
}
